import { Head, router } from "@inertiajs/react"
import { useEffect } from "react"
import { Card, CardContent } from "@/components/ui/card"

const POLL_INTERVAL_MS = 5000

// Peer-group prospects are NOT made to wait on a spinner: their report is
// emailed (the prospect_confirmation mailer carries the link + PDF). For that
// mode we show a calm confirmation with an opt-in link and skip the poll +
// auto-redirect. Members (full / wheel_only) keep the live "appears
// automatically" behavior.
function PeerGroupConfirmation({ status, publicToken, coachName }) {
  const coach = coachName || "your Kingdom Factor coach"

  if (status === "generation_failed") {
    return (
      <Confirmation title="Thank you — your coach will follow up">
        <p className="text-zinc-600">
          We hit a snag preparing your report automatically. {coach} will review
          your responses personally and be in touch within 24 hours.
        </p>
      </Confirmation>
    )
  }

  return (
    <Confirmation title="Thank you — your report is on its way">
      <p className="text-zinc-600">
        We're writing your personalized report now and will email it to you in
        the next few minutes. Keep an eye on your inbox (and your spam folder,
        just in case) — {coach} receives a copy too.
      </p>
      <p className="text-sm text-zinc-500">You can safely close this tab.</p>
      <div className="pt-2">
        <a
          href={`/reports/${publicToken}`}
          className="inline-block font-medium text-kf-red hover:underline"
        >
          Prefer to read it here? Open your report →
        </a>
      </div>
    </Confirmation>
  )
}

function Confirmation({ title, children }) {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <Head title="Thank you" />
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center space-y-5">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-kf-red/10 flex items-center justify-center">
              <svg className="w-7 h-7 text-kf-red" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="2" y="4" width="20" height="16" rx="2" />
                <path d="m22 7-10 6L2 7" />
              </svg>
            </div>
          </div>
          <h1 className="font-display text-2xl text-zinc-900">{title}</h1>
          {children}
        </CardContent>
      </Card>
    </div>
  )
}

export default function Submitted({ status, publicToken, assessmentMode, coachName }) {
  const isPeerGroup = assessmentMode === "peer_group"

  useEffect(() => {
    if (isPeerGroup) return // emailed delivery — no poll, no auto-redirect
    if (status === "generated") {
      if (typeof window !== "undefined") {
        window.location.href = `/reports/${publicToken}`
      }
      return
    }
    if (status === "generation_failed") {
      return
    }
    const id = setInterval(() => {
      router.reload({ only: ["status", "publicToken"], preserveScroll: true })
    }, POLL_INTERVAL_MS)
    return () => clearInterval(id)
  }, [isPeerGroup, status, publicToken])

  if (isPeerGroup) {
    return <PeerGroupConfirmation status={status} publicToken={publicToken} coachName={coachName} />
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <Head title="Generating your report" />

      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            <div className="relative">
              <div className="w-16 h-16 rounded-full border-4 border-zinc-200" />
              <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-kf-red animate-spin" />
            </div>
          </div>

          {status === "generation_failed" ? (
            <>
              <h1 className="font-display text-2xl text-zinc-900">
                Your coach will review personally
              </h1>
              <p className="text-zinc-600">
                We couldn't auto-generate your report this time. Your coach will
                review your responses and be in touch within 24 hours.
              </p>
            </>
          ) : (
            <>
              <h1 className="font-display text-2xl text-zinc-900">
                Your report is being written
              </h1>
              <p className="text-zinc-600">
                This usually takes 1–2 minutes. You can wait here and it'll
                appear automatically, or close this tab and check your email
                in a couple minutes — we'll send the full report there too.
              </p>

              <div className="border-t border-zinc-100 pt-5 text-left">
                <p className="text-xs uppercase tracking-wide text-zinc-500 mb-2">
                  What's coming
                </p>
                <ul className="text-sm text-zinc-700 space-y-1">
                  <li>— Executive summary</li>
                  <li>— Integrated Life Audit commentary</li>
                  <li>— Category-by-category narrative</li>
                  <li>— Suggested 30-day focus areas</li>
                  <li>— A note from your coach</li>
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
